import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';

const MODES = [
  { id: 'all', label: 'All', icon: '≡' },
  { id: 'protein', label: 'Protein', icon: '💪' },
  { id: 'calorie', label: 'Low Cal', icon: '📉' },
  { id: 'efficient', label: 'Efficient', icon: '⚡' },
];

export default function MobileMenu({ user }) {
  const { name } = useParams();
  const navigate = useNavigate();
  const restaurantName = decodeURIComponent(name);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('all');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [order, setOrder] = useState([]);
  const [showOrder, setShowOrder] = useState(false);
  const [restaurantColor, setRestaurantColor] = useState('#3a7d00');
  const [restaurantEmoji, setRestaurantEmoji] = useState('🍽️');

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      const { data: restaurant } = await supabase
        .from('restaurants').select('id, color, emoji').eq('name', restaurantName).single();
      if (restaurant) {
        setRestaurantColor(restaurant.color || '#3a7d00');
        setRestaurantEmoji(restaurant.emoji || '🍽️');
        const { data } = await supabase
          .from('menu_items').select('*').eq('restaurant_id', restaurant.id).order('name');
        setItems(data || []);
      }
      setLoading(false);
    };
    fetch();
  }, [restaurantName]);

  const categories = ['All', ...new Set(items.map(i => i.category).filter(Boolean))];

  const filtered = (() => {
    let f = [...items];
    if (mode === 'protein') f = f.sort((a, b) => b.protein - a.protein).slice(0, 30);
    else if (mode === 'calorie') f = f.filter(i => i.calories < 400);
    else if (mode === 'efficient') f = f.filter(i => i.calories > 0).sort((a, b) => (b.protein / b.calories) - (a.protein / a.calories));
    if (category !== 'All') f = f.filter(i => i.category === category);
    if (search) f = f.filter(i => i.name.toLowerCase().includes(search.toLowerCase()));
    return f;
  })();

  const totals = order.reduce((acc, item) => ({
    cal: acc.cal + (item.calories || 0),
    protein: acc.protein + (Number(item.protein) || 0),
    carbs: acc.carbs + (Number(item.total_carbs) || 0),
    fat: acc.fat + (Number(item.total_fat) || 0),
  }), { cal: 0, protein: 0, carbs: 0, fat: 0 });

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5', fontFamily: 'Inter, system-ui, sans-serif', paddingBottom: order.length > 0 ? '80px' : '0' }}>

      {/* Header */}
      <div style={{ background: `linear-gradient(135deg, ${restaurantColor}, ${restaurantColor}cc)`, padding: '3rem 1.25rem 1rem', position: 'sticky', top: 0, zIndex: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
          <button onClick={() => navigate('/')} style={{
            background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff',
            borderRadius: '50%', width: '34px', height: '34px', fontSize: '1rem',
            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
          }}>←</button>
          <span style={{ fontSize: '1.5rem' }}>{restaurantEmoji}</span>
          <div style={{ color: '#fff', fontWeight: 800, fontSize: '1.2rem', letterSpacing: '-0.02em' }}>{restaurantName}</div>
        </div>
        {/* Search */}
        <div style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: '0.85rem', top: '50%', transform: 'translateY(-50%)', fontSize: '0.9rem' }}>🔍</span>
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder={`Search ${restaurantName}...`}
            style={{
              width: '100%', padding: '0.65rem 1rem 0.65rem 2.4rem',
              borderRadius: '10px', border: 'none', fontSize: '0.9rem',
              background: 'rgba(255,255,255,0.95)', color: '#111',
              boxSizing: 'border-box', outline: 'none',
            }}
          />
        </div>
      </div>

      {/* Mode tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', padding: '0.75rem 1rem', overflowX: 'auto', background: '#fff', borderBottom: '1px solid #eee', WebkitOverflowScrolling: 'touch' }}>
        {MODES.map(m => (
          <button key={m.id} onClick={() => setMode(m.id)} style={{
            padding: '0.45rem 0.9rem', borderRadius: '20px', border: 'none',
            background: mode === m.id ? restaurantColor : '#f0f0f0',
            color: mode === m.id ? '#fff' : '#555',
            fontWeight: 600, fontSize: '0.82rem', whiteSpace: 'nowrap',
            cursor: 'pointer', flexShrink: 0,
          }}>{m.icon} {m.label}</button>
        ))}
      </div>

      {/* Category pills */}
      {categories.length > 1 && (
        <div style={{ display: 'flex', gap: '0.4rem', padding: '0.6rem 1rem', overflowX: 'auto', background: '#fff', borderBottom: '1px solid #eee', WebkitOverflowScrolling: 'touch' }}>
          {categories.map(c => (
            <button key={c} onClick={() => setCategory(c)} style={{
              padding: '0.3rem 0.75rem', borderRadius: '20px', border: `1px solid ${category === c ? restaurantColor : '#ddd'}`,
              background: category === c ? `${restaurantColor}15` : '#fff',
              color: category === c ? restaurantColor : '#666',
              fontWeight: 500, fontSize: '0.78rem', whiteSpace: 'nowrap',
              cursor: 'pointer', flexShrink: 0,
            }}>{c}</button>
          ))}
        </div>
      )}

      {/* Items */}
      <div style={{ padding: '0.75rem 1rem' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#888' }}>Loading menu...</div>
        ) : (
          <>
            <div style={{ fontSize: '0.75rem', color: '#aaa', marginBottom: '0.6rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{filtered.length} items</div>
            {filtered.map(item => (
              <div key={item.id} style={{
                background: '#fff', borderRadius: '14px', padding: '1rem',
                marginBottom: '0.6rem', boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.6rem' }}>
                  <div style={{ flex: 1, paddingRight: '0.5rem' }}>
                    <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#111', lineHeight: 1.3 }}>{item.name}</div>
                    {item.category && <div style={{ fontSize: '0.75rem', color: restaurantColor, fontWeight: 500, marginTop: '0.2rem' }}>{item.category}</div>}
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontWeight: 800, fontSize: '1.1rem', color: '#111' }}>{item.calories}</div>
                    <div style={{ fontSize: '0.65rem', color: '#aaa', textTransform: 'uppercase' }}>cal</div>
                  </div>
                </div>

                {/* Macro row */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.4rem', marginBottom: '0.75rem' }}>
                  {[['Protein', item.protein, 'g', '#3a7d00'], ['Carbs', item.total_carbs, 'g', '#e06c00'], ['Fat', item.total_fat, 'g', '#666']].map(([label, val, unit, color]) => (
                    <div key={label} style={{ background: '#f8f8f8', borderRadius: '8px', padding: '0.4rem', textAlign: 'center' }}>
                      <div style={{ fontWeight: 700, fontSize: '0.9rem', color }}>{val}{unit}</div>
                      <div style={{ fontSize: '0.65rem', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => setOrder(prev => [...prev, item])}
                  style={{
                    width: '100%', padding: '0.6rem',
                    background: `linear-gradient(135deg, ${restaurantColor}, ${restaurantColor}cc)`,
                    color: '#fff', border: 'none', borderRadius: '10px',
                    fontWeight: 600, fontSize: '0.88rem', cursor: 'pointer',
                  }}
                >+ Add to Order</button>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Floating order bar */}
      {order.length > 0 && (
        <div
          onClick={() => setShowOrder(true)}
          style={{
            position: 'fixed', bottom: '1rem', left: '1rem', right: '1rem',
            background: '#111', color: '#fff', borderRadius: '16px',
            padding: '1rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            boxShadow: '0 8px 32px rgba(0,0,0,0.25)', cursor: 'pointer', zIndex: 100,
          }}
        >
          <div>
            <div style={{ fontWeight: 700, fontSize: '1rem' }}>🛒 {order.length} items</div>
            <div style={{ fontSize: '0.78rem', color: '#aaa', marginTop: '0.15rem' }}>{totals.cal} cal · {totals.protein}g protein</div>
          </div>
          <div style={{ fontSize: '0.85rem', color: '#aaa' }}>View order ›</div>
        </div>
      )}

      {/* Order sheet */}
      {showOrder && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200,
          display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
        }} onClick={e => e.target === e.currentTarget && setShowOrder(false)}>
          <div style={{ background: '#fff', borderRadius: '20px 20px 0 0', padding: '1.5rem 1.25rem', maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>🛒 My Order</div>
              <button onClick={() => setShowOrder(false)} style={{ background: 'none', border: 'none', fontSize: '1.4rem', color: '#aaa', cursor: 'pointer' }}>×</button>
            </div>

            {order.map((item, idx) => (
              <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.6rem 0', borderBottom: '1px solid #f0f0f0' }}>
                <div style={{ flex: 1, fontSize: '0.9rem', color: '#111' }}>{item.name}</div>
                <div style={{ fontSize: '0.85rem', color: '#888', marginRight: '0.75rem' }}>{item.calories} cal</div>
                <button onClick={() => setOrder(prev => prev.filter((_, i) => i !== idx))} style={{ background: 'none', border: 'none', color: '#ccc', fontSize: '1.1rem', cursor: 'pointer' }}>×</button>
              </div>
            ))}

            {/* Totals */}
            <div style={{ marginTop: '1rem', background: '#f8f8f8', borderRadius: '12px', padding: '1rem' }}>
              <div style={{ fontWeight: 700, fontSize: '0.8rem', color: '#3a7d00', textTransform: 'uppercase', marginBottom: '0.75rem' }}>Totals</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                {[['Calories', totals.cal, ''], ['Protein', totals.protein, 'g'], ['Carbs', totals.carbs, 'g'], ['Fat', totals.fat, 'g']].map(([label, val, unit]) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                    <span style={{ color: '#888' }}>{label}</span>
                    <span style={{ fontWeight: 700 }}>{Math.round(val)}{unit}</span>
                  </div>
                ))}
              </div>
            </div>

            <button onClick={() => { setOrder([]); setShowOrder(false); }} style={{
              width: '100%', marginTop: '1rem', padding: '0.75rem',
              background: 'transparent', border: '1px solid #ddd', color: '#888',
              borderRadius: '10px', fontSize: '0.9rem', cursor: 'pointer',
            }}>Clear Order</button>
          </div>
        </div>
      )}
    </div>
  );
}
